import { ConversationParticipant } from '@/types/chat.type'

const normalizeParticipantName = (firstName: string, lastName: string): string => {
  return `${firstName} ${lastName}`.trim().toLowerCase()
}

const buildMentionLookup = (
  participants: ConversationParticipant[],
  currentUserId?: number
): {
  participantNameToId: Map<string, number>
  validParticipantIds: Set<number>
} => {
  const participantNameToId = new Map<string, number>()
  const validParticipantIds = new Set<number>()

  for (const participant of participants) {
    const participantId = participant.user.id
    if (participantId === currentUserId) {
      continue
    }

    validParticipantIds.add(participantId)

    const fullName = normalizeParticipantName(participant.user.firstName, participant.user.lastName)
    if (fullName) {
      participantNameToId.set(fullName, participantId)
    }
  }

  return { participantNameToId, validParticipantIds }
}

export const extractMentionedUserIdsFromContent = (
  content: string,
  participants: ConversationParticipant[],
  currentUserId?: number
): number[] => {
  if (!participants || participants.length === 0) {
    return []
  }

  if (!content || content.trim().length === 0) {
    return []
  }

  // Fast path: skip all parsing and participant lookup when message clearly has no mention markers.
  const hasMentionText = content.includes('@')
  const hasMentionDataAttribute = content.includes('data-mention-id')
  if (!hasMentionText && !hasMentionDataAttribute) {
    return []
  }

  const { participantNameToId, validParticipantIds } = buildMentionLookup(participants, currentUserId)
  const mentionedIds = new Set<number>()

  if (typeof window !== 'undefined') {
    try {
      const parser = new DOMParser()
      const doc = parser.parseFromString(content, 'text/html')
      const mentionElements = doc.querySelectorAll('[data-mention-id]')

      mentionElements.forEach(el => {
        const rawId = el.getAttribute('data-mention-id')
        if (!rawId) return

        const parsedId = Number(rawId)
        if (Number.isInteger(parsedId) && validParticipantIds.has(parsedId)) {
          mentionedIds.add(parsedId)
        }
      })
    } catch (_error) {
      // Ignore HTML parsing errors and fallback to plain text matching below
    }
  }

  const plainText = content
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  const mentionMatches = plainText.match(/@([a-zA-Z0-9._-]+(?:\s+[a-zA-Z0-9._-]+)?)/g) || []
  for (const match of mentionMatches) {
    const candidateName = match.slice(1).trim().toLowerCase()
    const participantId = participantNameToId.get(candidateName)
    if (participantId) {
      mentionedIds.add(participantId)
    }
  }

  return Array.from(mentionedIds)
}
